import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
    Button,
    PopupWindow,
    Table,
    MessagePopupCore,
} from '@utahdts/dts-react-common';
import Icon from '../misc/Icon';
import appStore from '../app/mobx/appStore';
import columns from './columns/WacColumns';
import webservice from '../app/webservice/webservice';
import Wac from './modals/Wac';
import * as Excel from 'exceljs';
import dates from '../misc/isoDate';
import security from '../app/roles/security';
import roles from '../app/roles/roles';
import { usePrevious } from '../misc/hooks/usePrevious';
import { sortByLastUpdatedDesc, visibleRecords } from '../misc/Shared';
import {
    formatTextFieldValue,
    normalizeExcelTextCell,
} from '../misc/textFieldFormatter';
import validateWacRow, { wacNdcKey } from './wac/validateWacRow';

const Wacs = () => {
    const [isBusy, setBusy] = useState(true);
    const [wacs, setWacs] = useState([]);
    const [showWac, displayWac] = useState(false);
    const [wac, setWac] = useState({});
    const [isNew, setIsNew] = useState(true);
    const [showDelete, displayDelete] = useState(false);
    // Only the setter is used (resetWac clears it); the value is not read.
    const [, setError] = useState(undefined);
    // WAC import FEIN gate: a WAC must be affiliated with a valid FEIN before a
    // file can be selected. Administrators have no single selected org for the
    // session, so they must pick one (which carries the FEIN) from a filterable
    // "Select Organization" list before the file picker opens.
    const [showOrgSelect, setShowOrgSelect] = useState(false);
    const [orgFilter, setOrgFilter] = useState('');
    const [uploadOrg, setUploadOrg] = useState(null);
    const [orgOptions, setOrgOptions] = useState([]);
    // After an Administrator picks an org in the "Select Organization" dialog,
    // immediately continue with the pending action: 'file' (open the upload file
    // picker) or 'manual' (open the manual WAC form).
    const [pendingUploadAction, setPendingUploadAction] = useState(null);
    const prevOrg = usePrevious(appStore.currentOrg);
    const fileInput = React.createRef();
    // The Administrator's chosen upload org, captured synchronously when the org
    // is selected so the (asynchronous) file-input onChange reads the right FEIN
    // regardless of React state timing.
    const uploadOrgRef = React.useRef(null);
    let files = [];

    // Returns the list of expected template column headers that are missing
    // from the uploaded file's header row. The downloadable template
    // (drug-wac-increase.xlsx) defines the full expected column set.
    const getMissingHeaders = (array) => {
        const headerRow = Array.isArray(array) ? array : [];
        return Object.keys(templateHeader)
            .filter((v) => !headerRow.includes(templateHeader[v].header))
            .map((v) => templateHeader[v].header);
    };

    const checkHeader = (array) => getMissingHeaders(array).length === 0;

    const checkNumber = (value) => {
        const normalized =
            typeof value === 'string'
                ? value.replace(/[$,\s]/g, '')
                : value;
        const number = Number(normalized);
        return Number.isFinite(number) ? number : false;
    };

    const checkDate = (date) => {
        // The WAC effective/increase date is a NAIVE calendar date (the day the
        // increase took effect). Read it as a literal MM/DD/YYYY WITHOUT any
        // timezone conversion — running it through moment().tz()/toISOString
        // shifts it a calendar day (or two, per the uploader's browser
        // timezone), which is the "off a day or so" drift users reported.
        return dates.toCalendarDate(date) || false;
    };

    const checkString = (value) => {
        // Never stringify an ExcelJS object (rich text/formula/hyperlink/error):
        // String(object) is the corrupt literal "[object Object]". Recognized
        // objects are normalized first; validation rejects everything else.
        return typeof value === 'string' || typeof value === 'number'
            ? String(value)
            : '';
    };

    const templateHeader = {
        ndc: {
            header: 'NDC',
            checker: checkString,
            type: 'string',
            isRequired: true,
        },
        drugDescription: {
            header: 'DRUG_PROD_DESC',
            checker: checkString,
            type: 'string',
            isRequired: true,
        },
        drugType: {
            header: 'DRUG_TYPE',
            checker: checkString,
            type: 'string',
            isRequired: true,
        },
        effectiveDate: {
            header: 'WAC_EFFECTIVE_DATE',
            checker: checkDate,
            type: 'date',
            isRequired: true,
        },
        increaseAmount: {
            header: 'WAC_INCREASE_AMOUNT',
            checker: checkString,
            type: 'string',
            isRequired: false,
        },
        wacAfterIncrease: {
            header: 'WAC_AFTER_INCREASE',
            checker: checkNumber,
            type: 'number',
            isRequired: false,
        },
        historyOneYear: {
            header: 'WAC_HISTORY_1YR',
            checker: checkNumber,
            type: 'number',
            isRequired: false,
        },
        historyTwoYear: {
            header: 'WAC_HISTORY_2YRS',
            checker: checkNumber,
            type: 'number',
            isRequired: false,
        },
        increaseFactors: {
            header: 'COST_INCREASE_FACTORS',
            checker: checkString,
            type: 'string',
            isRequired: true,
        },
        rdCosts: {
            header: 'RD_COSTS',
            // GraphQL models R&D costs as a String. Keep "0" and displayed
            // currency text intact instead of passing a number through the
            // string serializer (where numeric zero becomes blank).
            checker: checkString,
            type: 'string',
            isRequired: false,
        },
        comments: {
            header: 'GENERAL_COMMENTS',
            checker: checkString,
            type: 'string',
            isRequired: false,
        },
    };

    const fileSelected = () => {
        setBusy(true);
        const isAdmin = security.hasAnyRole([roles.dev, roles.admin]);
        // The persisted WAC must carry the affiliated organization's FEIN. An
        // Administrator's org is the one just chosen in the Select Organization
        // dialog (read from the ref so it is correct even though the file-input
        // onChange fires after the click that set it); an Org Administrator uses
        // their currently selected org.
        const adminOrg = uploadOrgRef.current;
        const uploadFein = isAdmin
            ? adminOrg
                ? adminOrg.fein
                : undefined
            : appStore.currentOrg || undefined;
        const uploadedFiles = fileInput.current.files;
        if (uploadedFiles.length === 0) {
            files = [];
        } else {
            files = [].concat(uploadedFiles[0]);
            appStore.filesNames = [].concat(
                Array.from(fileInput.current.files).map((file) => file.name),
            );
        }

        const keys = Object.keys(templateHeader);
        const workbook = new Excel.Workbook();
        const reader = new FileReader();
        reader.readAsArrayBuffer(files[0]);
        reader.onload = () => {
            const buffer = reader.result;
            workbook.xlsx.load(buffer).then((workbook) => {
                let sheet = workbook.worksheets[0];
                const headerRow = sheet.getRow(1);
                // Headers are text fields too. Format them before template
                // validation/index lookup so rich-text/formula/hyperlink header
                // cells behave exactly like pasted plain text.
                let header = headerRow.values.map((value, index) => {
                    if (index === 0) return value;
                    const cell =
                        typeof headerRow.getCell === 'function'
                            ? headerRow.getCell(index)
                            : { value };
                    const normalized = normalizeExcelTextCell(cell);
                    if (!normalized.supported) return value;
                    const formatted = formatTextFieldValue(normalized.value);
                    return formatted.supported ? formatted.value : value;
                });
                if (checkHeader(header)) {
                    let wacs = [];
                    const invalidColumns = new Set();
                    const seenNdcs = new Set();
                    const indexes = {};
                    keys.forEach(
                        (k) =>
                            (indexes[k] = header.findIndex(
                                (e) => e === templateHeader[k].header,
                            )),
                    );
                    sheet.eachRow((row) => {
                        // A header-keyed view of the raw row drives per-row
                        // validation (CLAUDE.md "WAC import"): every column is
                        // required except GENERAL_COMMENTS, with NDC / DRUG_TYPE
                        // / date / currency format checks.
                        const rawRow = {};
                        keys.forEach((k) => {
                            const cell = row.getCell(indexes[k]);
                            const originalValue = cell ? cell.value : undefined;
                            const normalized = normalizeExcelTextCell(cell);
                            // Recognized ExcelJS objects use their displayed text.
                            // Preserve unsupported objects for validateWacRow so
                            // the modal names their exact column.
                            rawRow[templateHeader[k].header] = normalized.supported
                                ? normalized.value
                                : originalValue;
                        });

                        // Skip the header row (its NDC cell holds the column
                        // label, not a value).
                        if (
                            typeof rawRow.NDC === 'string' &&
                            rawRow.NDC.trim().toUpperCase() === templateHeader.ndc.header
                        ) {
                            return;
                        }

                        const invalid = validateWacRow(rawRow, seenNdcs);
                        if (invalid.length) {
                            invalid.forEach((column) =>
                                invalidColumns.add(column),
                            );
                            return;
                        }

                        // Record this row's NDC so a later row carrying the
                        // same NDC is flagged as a duplicate.
                        seenNdcs.add(wacNdcKey(rawRow.NDC));

                        // Valid row: canonicalize the NDC (digits-only, padded
                        // to 11) and build the persisted WAC.
                        const ndcValue = String(rawRow.NDC);
                        let ndc = ndcValue.match(/\d+/g)
                            ? ndcValue.match(/\d+/g).join('')
                            : ndcValue;
                        while (ndc.length < 11) {
                            ndc = '0' + ndc.toString();
                        }
                        // The `org` relationship is the org FEIN (business
                        // key), never the document id (see
                        // .claude/rules/03-relationship-fields.md).
                        let wac = { org: uploadFein, fein: uploadFein };
                        keys.forEach((k) => {
                            const value = templateHeader[k].checker(
                                rawRow[templateHeader[k].header],
                            );
                            wac[k] = k === 'ndc' ? ndc : value;
                        });
                        wacs.push(wac);
                    });

                    // CLAUDE.md: on ANY invalid column value the file is NOT
                    // persisted and the modal names the specific invalid
                    // columns as the required fixes.
                    if (invalidColumns.size > 0) {
                        setBusy(false);
                        MessagePopupCore.addMessage({
                            title: 'Invalid data',
                            message: (
                                <div>
                                    <p>
                                        The uploaded file was not imported. Fix the
                                        following column(s) and upload again:
                                    </p>
                                    <ul>
                                        {[...invalidColumns].map((c) => (
                                            <li key={c}>{c}</li>
                                        ))}
                                    </ul>
                                </div>
                            ),
                        });
                        return;
                    }

                    if (wacs.length > 0) {
                        let promises = [];
                        wacs.forEach((w) => promises.push(webservice.wac.save(w)));
                        Promise.all(promises)
                            .then(() => {
                                getWacs();
                                MessagePopupCore.addMessage({
                                    title: 'Data uploaded',
                                    message: (
                                        <p>
                                            The current drug listing has been updated successfully.
                                            <br />
                                            {wacs.length} records added.
                                        </p>
                                    ),
                                });
                            })
                            .catch(displayError);
                    } else {
                        setBusy(false);
                        MessagePopupCore.addMessage({
                            title: 'Invalid data',
                            message: (
                                <p>
                                    Some data might be missing or formatted incorrectly. <br />
                                    Please make sure the file and its content are correct.
                                </p>
                            ),
                        });
                    }
                } else {
                    setBusy(false);
                    const missingHeaders = getMissingHeaders(header);
                    MessagePopupCore.addMessage({
                        title: 'Invalid template',
                        message: (
                            <div>
                                <p>
                                    The uploaded file is missing required column(s). Please
                                    download the example template and make sure the following
                                    fields are present in the header row:
                                </p>
                                <ul>
                                    {missingHeaders.map((h) => (
                                        <li key={h}>{h}</li>
                                    ))}
                                </ul>
                            </div>
                        ),
                    });
                }
            });
        };
    };

    const displayError = (error) => {
        console.error('[WACs] displayError called with error:', error);
        if (error) {
            console.error('[WACs] Error details:', {
                message: error.message,
                stack: error.stack,
            });
        } else {
            console.warn(
                '[WACs] displayError was called, but no error object was provided.',
            );
        }
        setBusy(false);
        MessagePopupCore.addMessage({
            title: 'Something went wrong',
            message: 'An error occurred. Please try again later.',
        });
    };

    // The org whose FEIN this WAC belongs to: an Administrator uses the org
    // chosen via the "Select Organization" dialog (passed in, or the stored
    // uploadOrg); an Org Administrator uses the currently selected org.
    const activeOrg = (org) =>
        security.hasAnyRole([roles.dev, roles.admin])
            ? org || uploadOrg || null
            : appStore.currentOrgRecord || null;
    const activeFein = (org) => {
        const o = activeOrg(org);
        return o ? o.fein : undefined;
    };

    const editWac = (isNew, wac, showWac, org) => {
        console.log('[WACs] editWac called', { isNew, wac, showWac });
        setIsNew(isNew);
        const fein = activeFein(org);
        wac.ndc = wac.ndc ? wac.ndc : undefined;
        // The WAC's `org` relationship and its `fein` are the org's FEIN
        // (business key). saveWAC REQUIRES `fein` (see
        // .claude/rules/06-wac-import.md). An existing WAC carries its org FEIN
        // in `org` (the wac GraphQL type has no `fein` field), while a new WAC
        // takes the actively-selected org's FEIN. Resolve one canonical value
        // and stamp both fields so editing an existing row (where no org is
        // actively selected — e.g. an Administrator editing a WAC) still
        // submits a valid FEIN.
        const resolvedFein = wac.fein || wac.org || fein;
        wac.org = resolvedFein;
        wac.fein = resolvedFein;
        console.log('[WACs] Setting WAC:', wac);
        setWac(wac);
        displayWac(showWac);
    };

    const resetWac = () => {
        console.log('[WACs] resetWac called');
        editWac(true, {}, false);
        setError(undefined);
        displayDelete(false);
    };

    const deleteWac = (wac) => {
        console.log('[Delete WAC] Initiating delete for WAC:', wac);
        console.log('[Delete WAC] WAC ID:', wac ? wac.id : undefined);
        setWac(wac);
        displayDelete(true);
    };

    const actualDelete = () => {
        console.log('[Delete WAC] Executing actual delete for WAC:', wac);
        console.log('[Delete WAC] WAC ID being deleted:', wac ? wac.id : undefined);

        return webservice.wac
            .deleteWac(wac)
            .then((result) => {
                console.log('[Delete WAC] Delete successful, result:', result);
                setBusy(true);
                resetWac();
                getWacs();
                return result;
            })
            .catch((error) => {
                console.error('[Delete WAC] Delete failed with error:', error);
                console.error('[Delete WAC] Error details:', {
                    message: error.message,
                    stack: error.stack,
                    wacId: wac ? wac.id : undefined,
                });
                displayError(error);
            });
    };

    // ---------------------------------------------------------------------
    // HELPER: Formats a WAC object for Display (UI)
    // Ensures all currency fields are strings rounded to 2 decimals
    // ---------------------------------------------------------------------
    const formatForDisplay = (wacItem) => {
        if (!wacItem) return wacItem;

        const formatted = { ...wacItem };
        const currencyFields = [
            'increaseAmount',
            'wacAfterIncrease',
            'rdCosts',
            'historyOneYear',
            'historyTwoYear',
        ];

        currencyFields.forEach((field) => {
            const val = formatted[field];
            if (val !== null && val !== undefined && val !== '') {
                // 1. Strip $ and , (in case bad data exists in DB)
                const str = val.toString().replace(/[$,]/g, '');
                // 2. Parse to float
                const num = parseFloat(str);
                if (!isNaN(num)) {
                    // 3. Round to 2 decimals (e.g., 52.7899 -> "52.79")
                    formatted[field] = num.toFixed(2);
                }
            }
        });

        return formatted;
    };

    // ---------------------------------------------------------------------
    // SAVE LOGIC: Sanitizes Input before sending to API
    // ---------------------------------------------------------------------
    const saveWac = (wacInput) => {
        console.log('[WACs] saveWac called with raw WAC:', wacInput);

        const sanitizeNumber = (val) => {
            if (val === null || val === undefined || val === '') return null;
            let num = val;
            if (typeof val === 'string') {
                const str = val.replace(/[$,]/g, '');
                num = parseFloat(str);
            }
            if (isNaN(num)) return null;
            // Round to 2 decimals for saving
            return Number(num.toFixed(2));
        };

        const cleanWac = { ...wacInput };

        cleanWac.increaseAmount = sanitizeNumber(cleanWac.increaseAmount);
        cleanWac.wacAfterIncrease = sanitizeNumber(cleanWac.wacAfterIncrease);
        cleanWac.rdCosts = sanitizeNumber(cleanWac.rdCosts);
        cleanWac.historyOneYear = sanitizeNumber(cleanWac.historyOneYear);
        cleanWac.historyTwoYear = sanitizeNumber(cleanWac.historyTwoYear);

        if (cleanWac.effectiveDate) {
            // Keep the effective date as a literal MM/DD/YYYY calendar date.
            // Converting it to an instant (moment().toISOString()) reinterprets
            // this naive date in the browser timezone and shifts it a day.
            cleanWac.effectiveDate = dates.toCalendarDate(cleanWac.effectiveDate);
        }

        console.log('[WACs] Sending cleaned WAC to API:', cleanWac);

        return webservice.wac
            .save(cleanWac)
            .then((result) => {
                console.log('[WACs] Save successful, result:', result);
                setBusy(true);
                resetWac();
                getWacs();
                return result;
            })
            .catch((error) => {
                console.error('[WACs] Save failed with error:', error);
                console.error('[WACs] Error details:', {
                    message: error.message,
                    stack: error.stack,
                    wac: cleanWac,
                });
                displayError(error);
            });
    };

    // ---------------------------------------------------------------------
    // GET LOGIC: Fetches data and formats it for the UI table
    // ---------------------------------------------------------------------
    const getWacs = () => {
        console.log('[WACs] getWacs called');
        const hasDevAdmin = security.hasAnyRole([roles.dev, roles.admin]);
        const hasOrgAdmin = security.hasAnyRole([roles.orgAdmin]);

        if (hasDevAdmin) {
            setBusy(true);
            webservice.wac
                .all()
                .then((data) => {
                    const wacsData = data.wacs || [];
                    // Clean data before displaying; never show hidden records.
                    const displayData = visibleRecords(wacsData).map(formatForDisplay);
                    setWacs(displayData);
                    setBusy(false);
                })
                .catch((error) => {
                    console.error('[WACs] Error fetching ALL WACs:', error);
                    displayError(error);
                });
        } else if (hasOrgAdmin && appStore.currentOrg) {
            setBusy(true);
            webservice.wac
                .perOrg(appStore.currentOrg)
                .then((data) => {
                    const wacsData = data.wacsByOrg || [];
                    // Clean data before displaying; never show hidden records.
                    const displayData = visibleRecords(wacsData).map(formatForDisplay);
                    setWacs(displayData);
                    setBusy(false);
                })
                .catch((error) => {
                    console.error('[WACs] Error fetching perOrg WACs:', error);
                    displayError(error);
                });
        } else {
            setWacs([]);
            setBusy(false);
        }
    };

    useEffect(() => {
        if (prevOrg !== appStore.currentOrg) {
            setBusy(true);
            getWacs();
        }
    }, [appStore.currentOrg]);

    useEffect(() => {
        getWacs();
    }, []);

    // The full org list (with FEINs) available to an Administrator for the
    // "Select Organization" upload dropdown.
    const allOrgs = () =>
        appStore.orgs && appStore.orgs.length ? appStore.orgs : orgOptions;

    const openFilePicker = () => {
        if (fileInput.current) {
            fileInput.current.click();
        }
    };

    const ensureOrgsLoaded = () => {
        if ((appStore.orgs && appStore.orgs.length) || orgOptions.length) {
            return;
        }
        const result =
            webservice.company &&
            typeof webservice.company.all === 'function' &&
            webservice.company.all();
        if (result && typeof result.then === 'function') {
            result
                .then((data) => setOrgOptions((data && data.orgs) || []))
                .catch(() => {});
        }
    };

    // Gate the file picker on a valid FEIN. An Administrator (no single selected
    // org) must choose an organization first; choosing it immediately opens the
    // file picker. An Org Administrator uploads against their selected org's FEIN.
    const handleUploadClick = () => {
        // An Administrator must choose the org every time (they have no single
        // selected org); selecting it opens the file picker immediately.
        if (security.hasAnyRole([roles.dev, roles.admin])) {
            ensureOrgsLoaded();
            setPendingUploadAction('file');
            setShowOrgSelect(true);
            return;
        }
        if (!appStore.currentOrg) {
            MessagePopupCore.addMessage({
                title: 'Select an organization',
                message:
                    'Select an organization with a valid FEIN before uploading a WAC file.',
            });
            return;
        }
        openFilePicker();
    };

    // Manually enter a WAC. An Administrator must choose an organization first
    // (the same "Select Organization" dialog), which immediately opens the
    // manual WAC form for the selected org's FEIN. An Org Administrator uses
    // their currently selected org's FEIN.
    const handleManualClick = () => {
        // An Administrator must choose the org every time; selecting it opens
        // the manual WAC form immediately for that org.
        if (security.hasAnyRole([roles.dev, roles.admin])) {
            ensureOrgsLoaded();
            setPendingUploadAction('manual');
            setShowOrgSelect(true);
            return;
        }
        if (!appStore.currentOrg) {
            MessagePopupCore.addMessage({
                title: 'Select an organization',
                message:
                    'Select an organization with a valid FEIN before entering a WAC.',
            });
            return;
        }
        editWac(true, {}, true);
    };

    const chooseUploadOrg = (org) => {
        // Capture the org synchronously so the file-input onChange reads the
        // right FEIN, and keep it in state for display/fallback.
        uploadOrgRef.current = org;
        setUploadOrg(org);
        setShowOrgSelect(false);
        const action = pendingUploadAction;
        setPendingUploadAction(null);
        // The FEIN is now known; trigger the pending action immediately. The
        // file picker is opened SYNCHRONOUSLY inside this click handler (a user
        // gesture) so the browser does not block the programmatic input click —
        // a deferred (setTimeout) click loses the user activation and is ignored
        // until the next manual click.
        if (action === 'manual') {
            editWac(true, {}, true, org);
        } else {
            openFilePicker();
        }
    };

    const filteredOrgs = () => {
        const needle = orgFilter.trim().toLowerCase();
        return allOrgs().filter(
            (o) =>
                !needle ||
                (o.name && o.name.toLowerCase().includes(needle)) ||
                (o.fein && String(o.fein).toLowerCase().includes(needle)),
        );
    };

    return (
        <div key="listings" className="home-wrapper">
            <div className="home-content">
                <h1 className="mb-zero">Drug Filings (WAC)</h1>
            </div>
            <div className="buttons flex">
                <Button
                    label="Download Template"
                    className="mr-spacing flex align-items-center"
                    onClick={() => (window.location.href = './drug-wac-increase.xlsx')}
                    children={
                        <span className="icon--round ml-spacing">{Icon.iconDownload()}</span>
                    }
                    childrenPostion={Button.CHILDREN_POSITION.AFTER}
                />
                <input
                    type="file"
                    style={{ display: 'none' }}
                    ref={fileInput}
                    onChange={fileSelected}
                    id="filePicker"
                    name="filePicker"
                    accept=".xlsx"
                    onClick={(e) => (e.target.value = null)}
                />
                <Button
                    label="Upload WAC"
                    className="mr-spacing flex align-items-center"
                    onClick={handleUploadClick}
                    children={
                        <span className="icon--round ml-spacing">{Icon.iconUpload()}</span>
                    }
                    childrenPostion={Button.CHILDREN_POSITION.AFTER}
                    busy={isBusy}
                />
                <Button
                    label="Manually Enter WAC"
                    className="mr-spacing flex align-items-center"
                    onClick={handleManualClick}
                    children={
                        <span className="icon--round ml-spacing">{Icon.iconPlus()}</span>
                    }
                    childrenPostion={Button.CHILDREN_POSITION.AFTER}
                />
            </div>
            {showOrgSelect && (
                <div className="wac-org-select" data-testid="wac-org-select">
                    <h3>Select Organization</h3>
                    <p>
                        Choose the organization (and its FEIN) this WAC belongs to
                        before continuing.
                    </p>
                    <input
                        type="text"
                        aria-label="Filter organizations"
                        placeholder="Filter organizations"
                        value={orgFilter}
                        onChange={(e) => setOrgFilter(e.target.value)}
                    />
                    <ul className="wac-org-select__list">
                        {filteredOrgs().map((o) => (
                            <li key={o.id}>
                                <Button
                                    label={`${o.name} (${o.fein})`}
                                    onClick={() => chooseUploadOrg(o)}
                                />
                            </li>
                        ))}
                    </ul>
                    <Button
                        label="Cancel"
                        className="button--black"
                        onClick={() => setShowOrgSelect(false)}
                    />
                </div>
            )}
            <div>
                <Table
                    list={sortByLastUpdatedDesc(wacs)}
                    columns={columns(editWac, deleteWac)}
                    filters={true}
                    showLimit={50}
                    ajaxSpinner={isBusy}
                />
            </div>
            {showWac && (
                <Wac onClose={resetWac} onSave={saveWac} isNew={isNew} wac={wac} />
            )}
            {showDelete && (
                <PopupWindow
                    className="popup-window--large"
                    onCloseButtonCallback={resetWac}
                    footerChildren={[
                        <Button
                            label="Cancel"
                            onClick={resetWac}
                            key="close-button"
                            className="button--black"
                        />,
                        <Button
                            label="Delete"
                            busy={isBusy}
                            onClick={actualDelete}
                            key="save-button"
                        />,
                    ]}
                >
                    <h3>Are you sure?</h3>
                    <p>You are about to delete a WAC.</p>
                </PopupWindow>
            )}
        </div>
    );
};

export default observer(Wacs);
